import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Upload, Plus, Package, FolderTree, Loader2, ChevronUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';
import ProductAdminCard from './ProductAdminCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders every category (top-level and sub) that directly holds products, as an
 * accordion. Each node has its own "Download template", "Upload Excel" and
 * "Add product" actions that are scoped to the node's slug.
 */
const CategoryProductsAccordion = ({ categories, products, updateProduct, deleteProduct, duplicateProduct, onCreateProduct, onProductsReloaded }) => {
  const [openIds, setOpenIds] = useState(() => new Set());
  const [busySlug, setBusySlug] = useState(null);

  const { orderedNodes, productsBySlug, uncategorised } = useMemo(() => {
    const bySlug = new Map();
    for (const p of products) {
      const s = p.category || '__none';
      if (!bySlug.has(s)) bySlug.set(s, []);
      bySlug.get(s).push(p);
    }
    // Sort each category's products by admin-defined order asc, then by name.
    for (const arr of bySlug.values()) {
      arr.sort((a, b) => {
        const da = (a.order || 0) - (b.order || 0);
        return da !== 0 ? da : (a.name || '').localeCompare(b.name || '');
      });
    }
    const roots = categories.filter(c => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
    const subsByParent = new Map();
    for (const c of categories.filter(x => x.parent_id)) {
      const arr = subsByParent.get(c.parent_id) || [];
      arr.push(c);
      subsByParent.set(c.parent_id, arr);
    }
    for (const arr of subsByParent.values()) {
      arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    const flat = [];
    for (const root of roots) {
      flat.push({ ...root, depth: 0 });
      const subs = subsByParent.get(root.id) || [];
      for (const sub of subs) flat.push({ ...sub, depth: 1 });
    }
    const knownSlugs = new Set(categories.map(c => c.slug));
    const uncat = [];
    for (const [slug, arr] of bySlug.entries()) {
      if (slug !== '__none' && !knownSlugs.has(slug)) uncat.push({ slug, products: arr });
    }
    return { orderedNodes: flat, productsBySlug: bySlug, uncategorised: uncat };
  }, [categories, products]);

  const toggle = (id) => setOpenIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const openAll = () => setOpenIds(new Set(orderedNodes.map(n => n.id)));
  const collapseAll = () => setOpenIds(new Set());

  const catById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

  const buildBreadcrumb = (node) => {
    const parts = [node.name];
    let cur = node;
    const seen = new Set();
    while (cur.parent_id && !seen.has(cur.id)) {
      seen.add(cur.id);
      const parent = catById.get(cur.parent_id);
      if (!parent) break;
      parts.push(parent.name);
      cur = parent;
    }
    return parts.reverse().join(' - ');
  };

  const downloadTemplate = async (node) => {
    setBusySlug(node.slug);
    try {
      const token = localStorage.getItem('bq_token');
      const r = await fetch(`${BACKEND_URL}/api/admin/products/template?category=${encodeURIComponent(node.slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Full breadcrumb (e.g. "Building & Hardware - Ceiling Cladding") so admins can
      // spot the exact category at a glance.
      const breadcrumb = buildBreadcrumb(node);
      const safeName = breadcrumb.replace(/[\\/:*?"<>|]/g, '').trim() || node.slug;
      a.download = `${safeName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setBusySlug(null);
    }
  };

  const downloadAll = async () => {
    setBusySlug('__all__');
    try {
      const token = localStorage.getItem('bq_token');
      const r = await fetch(`${BACKEND_URL}/api/admin/products/template?scope=all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `dr-tools-products-all-${ymd}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${products.length} products`);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setBusySlug(null);
    }
  };

  const uploadExcel = async (slug, file) => {
    if (!file) return;
    setBusySlug(slug);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/admin/products/upload-excel?category=${encodeURIComponent(slug)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { created = 0, updated = 0, error_count = 0, errors = [] } = res.data;
      if (error_count > 0) {
        toast.error(`${created} created, ${updated} updated · ${error_count} row(s) rejected. First: ${errors[0]}`);
      } else {
        toast.success(`Imported: ${created} new, ${updated} updated`);
      }
      onProductsReloaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusySlug(null);
    }
  };

  const reorderProduct = async (pid, direction) => {
    try {
      await api.put(`/admin/products/${pid}/reorder?direction=${direction}`);
      onProductsReloaded?.();
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  const renderNode = (node) => {
    const rows = productsBySlug.get(node.slug) || [];
    const open = openIds.has(node.id);
    return (
      <div
        key={node.id}
        className="border border-neutral-200 rounded-sm bg-white"
        style={{ marginLeft: node.depth * 20 }}
        data-testid={`admin-cat-node-${node.slug}`}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-neutral-100">
          <button
            onClick={() => toggle(node.id)}
            className="text-neutral-500 hover:text-[#F46A25] flex items-center gap-2 flex-1 text-left"
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <div className="flex flex-col">
              <span className="text-sm font-bold text-[#12263A] flex items-center gap-1.5">
                {node.depth > 0 && <span className="text-neutral-300 text-xs">↳</span>}
                {node.name}
                <span className="text-[10px] font-mono text-neutral-400 font-normal">/{node.slug}</span>
              </span>
              <span className="text-[11px] text-neutral-500 flex items-center gap-1">
                <Package className="w-3 h-3" /> {rows.length} product{rows.length === 1 ? '' : 's'}
              </span>
            </div>
          </button>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => downloadTemplate(node)}
              disabled={busySlug === node.slug}
              data-testid={`admin-cat-download-${node.slug}`}
              className="text-[11px] bg-white border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] text-[#12263A] font-semibold px-2 py-1 rounded-sm flex items-center gap-1 disabled:opacity-60"
              title="Download Excel for this category"
            >
              {busySlug === node.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Template
            </button>
            <label
              data-testid={`admin-cat-upload-${node.slug}`}
              className={`text-[11px] bg-[#12263A] hover:bg-[#F46A25] text-white font-semibold px-2 py-1 rounded-sm flex items-center gap-1 cursor-pointer ${busySlug === node.slug ? 'opacity-60 pointer-events-none' : ''}`}
              title="Upload Excel scoped to this category"
            >
              <Upload className="w-3 h-3" /> Upload
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  uploadExcel(node.slug, f);
                  e.target.value = '';
                }}
              />
            </label>
            <button
              onClick={() => onCreateProduct(node.slug)}
              data-testid={`admin-cat-add-${node.slug}`}
              className="text-[11px] bg-[#F46A25] hover:bg-[#e05a15] text-white font-semibold px-2 py-1 rounded-sm flex items-center gap-1"
              title="Add a new product to this category"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
        {open && (
          <div className="p-3">
            {rows.length === 0 ? (
              <div className="text-xs text-neutral-500 py-6 text-center border border-dashed border-neutral-200 rounded-sm">
                No products in this category yet. Click <b>Add</b> above or use the Excel upload.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {rows.map((p, idx) => (
                  <div key={p.id} className="relative">
                    <div className="absolute -top-2 -left-2 z-10 flex gap-0.5">
                      <button
                        onClick={() => reorderProduct(p.id, 'up')}
                        disabled={idx === 0}
                        data-testid={`product-move-up-${p.id}`}
                        aria-label="Move product up"
                        className={`w-6 h-6 rounded-full flex items-center justify-center shadow-sm ${idx === 0 ? 'bg-neutral-100 text-neutral-300 cursor-not-allowed' : 'bg-white border border-neutral-300 text-[#12263A] hover:bg-[#F46A25] hover:text-white hover:border-[#F46A25]'}`}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => reorderProduct(p.id, 'down')}
                        disabled={idx === rows.length - 1}
                        data-testid={`product-move-down-${p.id}`}
                        aria-label="Move product down"
                        className={`w-6 h-6 rounded-full flex items-center justify-center shadow-sm ${idx === rows.length - 1 ? 'bg-neutral-100 text-neutral-300 cursor-not-allowed' : 'bg-white border border-neutral-300 text-[#12263A] hover:bg-[#F46A25] hover:text-white hover:border-[#F46A25]'}`}
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <ProductAdminCard
                      p={p}
                      categories={categories}
                      updateProduct={updateProduct}
                      deleteProduct={deleteProduct}
                      duplicateProduct={duplicateProduct}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <FolderTree className="w-4 h-4 text-[#F46A25]" />
          <span>{orderedNodes.length} categories · {products.length} total products</span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            onClick={downloadAll}
            disabled={busySlug === '__all__'}
            data-testid="admin-download-all-products"
            className="border border-[#F46A25] bg-orange-50 hover:bg-orange-100 text-[#F46A25] px-2.5 py-1 rounded-sm font-bold flex items-center gap-1 disabled:opacity-60"
            title="Download every product in the catalogue as one Excel file"
          >
            {busySlug === '__all__' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Download all products
          </button>
          <button onClick={openAll} className="border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] px-2.5 py-1 rounded-sm font-semibold">Expand all</button>
          <button onClick={collapseAll} className="border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] px-2.5 py-1 rounded-sm font-semibold">Collapse all</button>
        </div>
      </div>
      <div className="space-y-2">
        {orderedNodes.map(renderNode)}
      </div>

      {uncategorised.length > 0 && (
        <div className="mt-4 border border-amber-300 bg-amber-50 rounded-sm p-3 text-xs">
          <div className="font-bold text-amber-800 mb-1">Products with unknown category slug</div>
          <ul className="list-disc ml-4 space-y-0.5 text-amber-900">
            {uncategorised.map(u => (
              <li key={u.slug}>
                <span className="font-mono">{u.slug}</span> — {u.products.length} product(s). Re-assign them or create a matching category in the Categories tab.
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CategoryProductsAccordion;
